import {
  CheckCircle2,
  FileText,
  HelpCircle,
  Mail,
  Users as MeetingIcon,
  MessageSquare,
  Phone,
  Plus,
  Send,
  StickyNote,
} from 'lucide-react';
import { useState } from 'react';
import { EmailAttachmentList } from '@/components/email/EmailAttachmentList';
import { Spinner } from '@/components/ui';
import { Button, ModalContentWrapper } from '@/components/ui';
import {
  COMMUNICATION_STATUS_LABELS,
  COMMUNICATION_TYPE_LABELS,
  type CommunicationType,
  type LoanCommunication,
  useApplicationCommunications,
  useCommunications,
  useCreateApplicationCommunication,
  useCreateCommunication,
  useReplyApplicationCommunication,
  useReplyCommunication,
  useResolveApplicationCommunication,
  useResolveCommunication,
  useSendApplicationEmail,
} from '@/hooks/use-communications';
import { handleMutationError } from '@/lib/error-handling';

const TYPE_ICONS: Record<CommunicationType, any> = {
  EMAIL: Mail,
  PHONE_CALL: Phone,
  MEETING: MeetingIcon,
  LETTER: FileText,
  INTERNAL_NOTE: StickyNote,
  ENQUIRY: HelpCircle,
};

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ApplicationEmailThreadModal({
  communication,
  onClose,
}: {
  communication: LoanCommunication | null;
  onClose: () => void;
}) {
  if (!communication?.emailMessage) return null;

  const messages = [...(communication.emailThreadMessages || [communication.emailMessage])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  const primaryMessage = messages[messages.length - 1] || communication.emailMessage;
  const recipientLabel =
    primaryMessage.toAddresses.map(item => item.name || item.email).join(', ') || 'Loan Management';

  return (
    <ModalContentWrapper
      open
      onClose={onClose}
      variant="right"
      title={communication.emailMessage.subject}
      classes={{
        title: 'font-serif text-[28px] leading-[34px]',
        dialogPanel: 'max-w-[700px] h-screen',
        children: 'px-8 pb-8 pt-5 h-full overflow-y-auto',
      }}
    >
      <div className="space-y-6">
        <div className="space-y-4 border-b border-gray-200 pb-6">
          <div className="text-sm text-gray-600">
            <span className="text-gray-500">Type:</span>{' '}
            <span className="font-medium text-gray-900">
              {primaryMessage.direction === 'INBOUND' ? 'Received email' : 'Sent email'}
            </span>
            {communication.tags?.includes('OFFER_LETTER') && (
              <span className="ml-3 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-amber-700">
                Offer Letter
              </span>
            )}
          </div>
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="font-semibold text-gray-900">
                {primaryMessage.fromName || primaryMessage.fromAddress}
              </span>
              <span className="text-gray-500">&lt;{primaryMessage.fromAddress}&gt;</span>
              <span className="text-gray-500">
                {fmtDate(
                  primaryMessage.receivedAt || primaryMessage.sentAt || primaryMessage.createdAt,
                )}
              </span>
            </div>
            <p className="text-sm text-gray-500">to {recipientLabel}</p>
          </div>
        </div>

        <div className="space-y-4">
          {messages.map(message => (
            <div key={message.id} className="rounded-xl border border-gray-200 bg-white p-6">
              <div className="text-sm font-semibold text-gray-900">
                {message.fromName || message.fromAddress}
              </div>
              <p className="mt-1 text-sm text-gray-500">
                to{' '}
                {message.toAddresses.map(item => item.name || item.email).join(', ') ||
                  'Loan Management'}
              </p>
              {message.bodyText && (
                <p className="mt-5 whitespace-pre-wrap break-words text-base leading-8 text-gray-900">
                  {message.bodyText}
                </p>
              )}
              <EmailAttachmentList attachments={message.attachments} />
              <div className="mt-6 flex items-center justify-between gap-3 text-xs text-gray-500">
                <span>{fmtDate(message.receivedAt || message.sentAt || message.createdAt)}</span>
                <span className="rounded bg-gray-100 px-2 py-1 font-medium text-gray-600">
                  {message.direction === 'INBOUND' ? 'Received' : 'Sent'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </ModalContentWrapper>
  );
}

interface Props {
  loanId?: string;
  applicationId?: string;
  applicationNumber?: string;
  applicantEmail?: string;
}

export function CommunicationsTab({
  loanId,
  applicationId,
  applicationNumber = '',
  applicantEmail = '',
}: Props) {
  const isApplication = Boolean(applicationId);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const { data: loanComms, isLoading: loanLoading } = useCommunications(loanId ?? '', {
    type: typeFilter || undefined,
    status: statusFilter || undefined,
  });
  const { data: applicationComms, isLoading: applicationLoading } = useApplicationCommunications(
    applicationId ?? '',
    { type: typeFilter || undefined, status: statusFilter || undefined },
  );
  const comms = isApplication ? applicationComms : loanComms;
  const isLoading = isApplication ? applicationLoading : loanLoading;
  const createComm = useCreateCommunication(loanId ?? '');
  const replyComm = useReplyCommunication(loanId ?? '');
  const resolveComm = useResolveCommunication(loanId ?? '');
  const createApplicationComm = useCreateApplicationCommunication(applicationId ?? '');
  const sendApplicationEmail = useSendApplicationEmail(applicationId ?? '');
  const replyApplicationComm = useReplyApplicationCommunication(applicationId ?? '');
  const resolveApplicationComm = useResolveApplicationCommunication(applicationId ?? '');
  const [showCreate, setShowCreate] = useState(false);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedEmailCommunication, setSelectedEmailCommunication] =
    useState<LoanCommunication | null>(null);
  const [emailForm, setEmailForm] = useState({
    to: applicantEmail,
    subject: `Application Reference: ${applicationNumber}`,
    body: '',
  });

  const [form, setForm] = useState({
    type: 'EMAIL' as CommunicationType,
    direction: 'OUTBOUND',
    subject: '',
    body: '',
    contactName: '',
    fromEmail: '',
    toEmail: '',
    priority: 'NORMAL',
  });

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    (isApplication ? createApplicationComm : createComm).mutate(form, {
      onSuccess: () => {
        setShowCreate(false);
        setForm({ ...form, subject: '', body: '', contactName: '', fromEmail: '', toEmail: '' });
      },
    });
  }

  function handleReply(cid: string) {
    if (!replyText.trim()) return;
    (isApplication ? replyApplicationComm : replyComm).mutate(
      {
        cid,
        body: replyText,
        direction: 'OUTBOUND',
        subject: 'RE: ' + (comms?.find(c => c.id === cid)?.subject || ''),
      },
      {
        onSuccess: () => {
          setReplyTo(null);
          setReplyText('');
        },
      },
    );
  }

  async function handleSendApplicationEmail(e: React.FormEvent) {
    e.preventDefault();
    if (!emailForm.to.trim() || !emailForm.subject.trim() || !emailForm.body.trim()) return;
    try {
      await sendApplicationEmail.mutateAsync(emailForm);
      setShowEmailModal(false);
      setEmailForm(current => ({ ...current, body: '' }));
    } catch (error) {
      handleMutationError(error, 'Failed to send email');
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-900">Communications</h3>
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            className="text-xs border border-gray-300 rounded px-2 py-1"
          >
            <option value="">All Types</option>
            {Object.entries(COMMUNICATION_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="text-xs border border-gray-300 rounded px-2 py-1"
          >
            <option value="">All Statuses</option>
            {Object.entries(COMMUNICATION_STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            onClick={() => setShowCreate(!showCreate)}
            className="w-fit flex items-center gap-1.5 px-3 py-1.5 text-white text-sm rounded-md hover:"
          >
            <Plus className="w-3.5 h-3.5" /> Log Communication
          </Button>
          {isApplication && (
            <Button
              variant="secondary"
              onClick={() => setShowEmailModal(true)}
              className="w-fit flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md"
            >
              <Mail className="w-3.5 h-3.5" /> Send Email
            </Button>
          )}
        </div>
      </div>

      {isApplication && (
        <ModalContentWrapper
          open={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          title="Send Email"
        >
          <form onSubmit={handleSendApplicationEmail} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
              <input
                value={emailForm.to}
                onChange={e => setEmailForm({ ...emailForm, to: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                type="email"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
              <input
                value={emailForm.subject}
                onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
              <textarea
                value={emailForm.body}
                onChange={e => setEmailForm({ ...emailForm, body: e.target.value })}
                placeholder="Type your message here…"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                rows={6}
                required
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="primary"
                type="submit"
                disabled={
                  sendApplicationEmail.isPending ||
                  !emailForm.to ||
                  !emailForm.subject ||
                  !emailForm.body
                }
                className="flex-1 flex items-center justify-center gap-2"
              >
                <Send className="w-3.5 h-3.5" />{' '}
                {sendApplicationEmail.isPending ? 'Sending…' : 'Send Email'}
              </Button>
              <Button
                variant="secondary"
                type="button"
                onClick={() => setShowEmailModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </ModalContentWrapper>
      )}

      {/* Create form */}
      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Type *</label>
              <select
                value={form.type}
                onChange={e => setForm({ ...form, type: e.target.value as CommunicationType })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              >
                {Object.entries(COMMUNICATION_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Direction</label>
              <select
                value={form.direction}
                onChange={e => setForm({ ...form, direction: e.target.value })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              >
                <option value="OUTBOUND">Outbound</option>
                <option value="INBOUND">Inbound</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Priority</label>
              <select
                value={form.priority}
                onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              >
                <option value="LOW">Low</option>
                <option value="NORMAL">Normal</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Contact Name</label>
              <input
                value={form.contactName}
                onChange={e => setForm({ ...form, contactName: e.target.value })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Subject *</label>
            <input
              value={form.subject}
              onChange={e => setForm({ ...form, subject: e.target.value })}
              required
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              placeholder="Subject line or call summary"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Body / Notes</label>
            <textarea
              rows={3}
              value={form.body}
              onChange={e => setForm({ ...form, body: e.target.value })}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm resize-none"
              placeholder="Details of the communication..."
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="primary"
              type="submit"
              disabled={createComm.isPending}
              className="w-fit px-3 py-1.5 text-white text-sm rounded hover: disabled:opacity-50"
            >
              {createComm.isPending ? 'Creating…' : 'Log Communication'}
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={() => setShowCreate(false)}
              className="w-fit"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Communications list */}
      {comms && comms.length > 0 ? (
        <div className="space-y-2">
          {comms.map(c => {
            if (isApplication && c.emailMessage) {
              const recipient =
                c.emailMessage.toAddresses.map(item => item.name || item.email).join(', ') ||
                c.toEmail ||
                'Loan Management';
              const timestamp =
                c.emailMessage.receivedAt || c.emailMessage.sentAt || c.emailMessage.createdAt;
              const isInbound = c.emailMessage.direction === 'INBOUND';

              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedEmailCommunication(c)}
                  className="w-full rounded-xl border border-gray-200 bg-white p-4 text-left transition hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-semibold text-gray-900">
                          {c.emailMessage.subject}
                        </p>
                        {c.tags?.includes('OFFER_LETTER') && (
                          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-amber-700">
                            Offer Letter
                          </span>
                        )}
                      </div>
                      <p className="mt-1 truncate text-sm text-gray-500">{recipient}</p>
                      <p className="mt-2 line-clamp-1 text-sm text-gray-500">
                        {c.emailMessage.bodyText || c.body}
                      </p>
                    </div>
                    <div className="shrink-0 text-right text-xs text-gray-500">
                      <p>{fmtDate(timestamp)}</p>
                      <span
                        className={`mt-2 inline-flex rounded px-2 py-1 font-medium ${isInbound ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-600'}`}
                      >
                        {isInbound ? 'Received' : 'Sent'}
                      </span>
                    </div>
                  </div>
                </button>
              );
            }

            const Icon = TYPE_ICONS[c.type] || MessageSquare;
            const statusCfg = COMMUNICATION_STATUS_LABELS[c.status];
            return (
              <div key={c.id} className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-mono text-indigo-600 font-medium">
                      {c.referenceNo}
                    </span>
                    <span
                      className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full`}
                      style={{ backgroundColor: statusCfg.bg, color: statusCfg.color }}
                    >
                      {statusCfg.label}
                    </span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full ${c.direction === 'INBOUND' ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-600'}`}
                    >
                      {c.direction === 'INBOUND' ? '← In' : '→ Out'}
                    </span>
                    {c.priority === 'HIGH' && (
                      <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">
                        HIGH
                      </span>
                    )}
                    {c.priority === 'URGENT' && (
                      <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">
                        URGENT
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-400">{fmtDate(c.createdAt)}</span>
                    {c.status !== 'RESOLVED' && c.status !== 'CLOSED' && (
                      <button
                        onClick={() =>
                          (isApplication ? resolveApplicationComm : resolveComm).mutate(c.id)
                        }
                        className="text-[10px] text-green-600 hover:text-green-700"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                <p className="text-sm font-medium text-gray-900 mb-1">{c.subject}</p>
                {(c.body || c.emailMessage?.bodyText) && (
                  <details className="mb-2 text-xs text-gray-600">
                    <summary className="cursor-pointer text-indigo-600 hover:text-indigo-700">
                      Message details
                    </summary>
                    <p className="mt-2 whitespace-pre-wrap break-words">
                      {c.emailMessage?.bodyText || c.body}
                    </p>
                    <EmailAttachmentList attachments={c.emailMessage?.attachments} />
                  </details>
                )}

                {c.inboundEmailReplies && c.inboundEmailReplies.length > 0 && (
                  <div className="mt-4 space-y-4 border-t border-gray-100 pt-4">
                    {c.inboundEmailReplies.map(reply => (
                      <div
                        key={reply.id}
                        className="rounded-xl border border-gray-200 bg-white p-4"
                      >
                        <div className="text-sm font-semibold text-gray-900">
                          {reply.fromName || reply.fromAddress}
                        </div>
                        <p className="mt-1 text-sm text-gray-500">to Loan Management</p>
                        {reply.bodyText && (
                          <p className="mt-4 whitespace-pre-wrap break-words text-sm leading-6 text-gray-900">
                            {reply.bodyText}
                          </p>
                        )}
                        <EmailAttachmentList attachments={reply.attachments} />
                        <div className="mt-4 flex items-center justify-between gap-3 text-xs text-gray-500">
                          <span>{fmtDate(reply.receivedAt || reply.createdAt)}</span>
                          <span className="rounded bg-emerald-50 px-2 py-1 font-medium text-emerald-700">
                            Received
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-3 text-[10px] text-gray-400">
                  {c.contactName && <span>Contact: {c.contactName}</span>}
                  {c.toEmail && <span>To: {c.toEmail}</span>}
                  {c.tags?.includes('OFFER_LETTER') && (
                    <span className="rounded-full bg-amber-50 px-1.5 py-0.5 font-medium text-amber-700">
                      Offer letter
                    </span>
                  )}
                  {c.tags?.includes('PROPOSAL') && (
                    <span className="rounded-full bg-indigo-50 px-1.5 py-0.5 font-medium text-indigo-700">
                      Proposal
                    </span>
                  )}
                  {c.createdBy && (
                    <span>
                      By: {c.createdBy.firstName} {c.createdBy.lastName}
                    </span>
                  )}
                </div>

                {/* Reply */}
                {replyTo === c.id ? (
                  <div className="mt-2 flex gap-2">
                    <input
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      placeholder="Type reply…"
                      className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                    <Button
                      variant="primary"
                      onClick={() => handleReply(c.id)}
                      disabled={replyComm.isPending}
                      className="px-2 py-1.5 text-white text-xs rounded hover:"
                    >
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                    <button
                      onClick={() => setReplyTo(null)}
                      className="px-2 py-1.5 border border-gray-300 text-xs rounded"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setReplyTo(c.id)}
                    className="mt-2 text-[10px] text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Reply →
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-10 text-gray-400">
          <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No communications logged yet</p>
        </div>
      )}

      <ApplicationEmailThreadModal
        communication={selectedEmailCommunication}
        onClose={() => setSelectedEmailCommunication(null)}
      />
    </div>
  );
}
